// ─── normalizeUtils.js ──────────────────────────────────────────────────────

/**
 * DISPLAY / STORAGE normalization.
 * Use when SAVING a value (on blur, on select, on submit).
 * "midnapur cEnter bus Stand" → "Midnapur Center Bus Stand"
 */
export const toTitleCase = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^\s-]+/g, (word) =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    );
};

/**
 * TYPING normalization — safe to call on every keystroke.
 * Capitalises the first letter of each completed word (after a space)
 * but never swallows the space or touches the word currently being typed.
 *
 * "mid"            → "Mid"
 * "mid "           → "Mid "          ← space preserved
 * "mid na"         → "Mid Na"
 * "midnapur cEnter bus Stand" → "Midnapur Center Bus Stand"
 */
export const toTitleCaseLive = (str) => {
  if (str === null || str === undefined) return str;
  if (typeof str !== 'string') return str;

  // Walk character-by-character keeping spaces intact.
  // Capitalise the first non-space char after a space (or at position 0).
  let result = '';
  let capitaliseNext = true; // capitalise very first char

  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    if (ch === ' ') {
      result += ch;
      capitaliseNext = true;         // next word starts after this space
    } else if (capitaliseNext) {
      result += ch.toUpperCase();
      capitaliseNext = false;
    } else {
      // Leave the current char as-is while the user is still typing.
      // Once the word is "completed" (a space follows) it stays readable;
      // full normalisation happens on blur/select via toTitleCase.
      result += ch;
    }
  }
  return result;
};

/**
 * Returns a lowercase, whitespace-collapsed key for deduplication.
 * "Midnapur Center Bus Stand" and "midnapur  center bus stand" → same key.
 */
export const normalizeKey = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.trim().replace(/\s+/g, ' ').toLowerCase();
};

/**
 * Deduplicates an array of objects by a derived string key.
 * Later entries win (last-seen wins).
 */
export const deduplicateBy = (items, keyFn) => {
  const seen = new Map();
  items.forEach((item) => {
    const key = normalizeKey(keyFn(item));
    if (key) seen.set(key, item);
  });
  return Array.from(seen.values());
};

/**
 * Normalises every text field in a city/stop suggestion object
 * AND deduplicates the array in one call.
 *
 * Handles { city, name, address, terminal, state } shapes.
 */
export const normalizeAndDeduplicateSuggestions = (suggestions) => {
  if (!Array.isArray(suggestions)) return [];

  const normalized = suggestions.map((s) => ({
    ...s,
    city:     s.city     ? toTitleCase(s.city)     : undefined,
    name:     s.name     ? toTitleCase(s.name)     : undefined,
    address:  s.address  ? toTitleCase(s.address)  : undefined,
    terminal: s.terminal ? toTitleCase(s.terminal) : undefined,
    state:    s.state    ? toTitleCase(s.state)    : undefined,
  }));

  return deduplicateBy(normalized, (s) => s.city || s.name || '');
};